(() => {
  'use strict';

  if (globalThis.CraftdiyProductGalleryLightboxV1) return;
  globalThis.CraftdiyProductGalleryLightboxV1 = true;

  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const SCALE_STEP = 0.5;
  let activeRoot = null;
  let returnFocus = null;
  let lastStageOpen = 0;
  const pointers = new Map();
  const zoom = { scale: 1, x: 0, y: 0 };
  let panStart = null;
  let pinchStart = null;
  let gestureMoved = false;

  const dialog = document.createElement('dialog');
  dialog.className = 'cdg-lightbox';
  dialog.setAttribute('aria-label', 'Large product image');
  dialog.innerHTML = `
    <div class="cdg-lightbox__viewport" data-lightbox-viewport>
      <img class="cdg-lightbox__image" data-lightbox-image alt="" draggable="false">
    </div>
    <button type="button" class="cdg-lightbox__close" data-lightbox-close aria-label="Close large image">×</button>
    <button type="button" class="cdg-lightbox__arrow cdg-lightbox__arrow--prev" data-lightbox-prev aria-label="Previous image">‹</button>
    <button type="button" class="cdg-lightbox__arrow cdg-lightbox__arrow--next" data-lightbox-next aria-label="Next image">›</button>
    <div class="cdg-lightbox__zoom" role="group" aria-label="Image zoom controls">
      <button type="button" data-lightbox-zoom-out aria-label="Zoom out">−</button>
      <button type="button" class="cdg-lightbox__zoom-level" data-lightbox-zoom-reset aria-label="Fit image to screen"><span data-lightbox-zoom-level>100%</span></button>
      <button type="button" data-lightbox-zoom-in aria-label="Zoom in">＋</button>
    </div>
    <div class="cdg-lightbox__caption" data-lightbox-caption></div>
  `;
  document.body.appendChild(dialog);

  const viewport = dialog.querySelector('[data-lightbox-viewport]');
  const output = dialog.querySelector('[data-lightbox-image]');
  const caption = dialog.querySelector('[data-lightbox-caption]');
  const zoomLevel = dialog.querySelector('[data-lightbox-zoom-level]');

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function visibleTrackImage(stage) {
    const track = stage.querySelector('.cv9__swipe-track,.ccr__swipe-track');
    if (!track?.children?.length) return null;
    const index = track.clientWidth
      ? clamp(Math.round(track.scrollLeft / track.clientWidth), 0, track.children.length - 1)
      : 0;
    return track.children[index]?.querySelector('img') || null;
  }

  function fullSourceFor(root, image) {
    if (!image) return '';
    if (root.classList.contains('is-live-preview')) {
      return image.currentSrc || image.src || '';
    }
    const mediaId = image.dataset.mediaId || image.closest('[data-media-id]')?.dataset.mediaId || '';
    const thumbnail = [...root.querySelectorAll('[data-thumb]')]
      .find((item) => String(item.dataset.mediaId || '') === String(mediaId));
    return thumbnail?.dataset.srcDesktop
      || thumbnail?.dataset.src
      || image.currentSrc
      || image.src
      || '';
  }

  function activeGalleryImage(root) {
    const stage = root?.querySelector('[data-craftdiy-gallery-stage]');
    if (!stage) return null;

    if (stage.classList.contains('is-custom-preview')) {
      const sourceCanvas = root.querySelector('[data-preview-canvas]');
      if (sourceCanvas?.width && sourceCanvas?.height) {
        try {
          return {
            source: sourceCanvas.toDataURL('image/png'),
            alt: `${root.querySelector('h1')?.textContent?.trim() || 'Product'} live personalization preview`,
          };
        } catch (error) {
          const previewBase = root.querySelector('.craftdiy-layered-base');
          if (previewBase?.src) {
            return {
              source: previewBase.currentSrc || previewBase.src,
              alt: previewBase.alt || 'Product preview',
            };
          }
        }
      }
    }

    const image = visibleTrackImage(stage) || root.querySelector('[data-main-image]');
    const source = fullSourceFor(root, image);
    return source ? { source, alt: image?.alt || '' } : null;
  }

  function availableViewportSize() {
    const style = getComputedStyle(viewport);
    return {
      width: Math.max(1, viewport.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)),
      height: Math.max(1, viewport.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)),
    };
  }

  function constrainPosition() {
    if (zoom.scale <= MIN_SCALE || !output.offsetWidth || !output.offsetHeight) {
      zoom.x = 0;
      zoom.y = 0;
      return;
    }
    const available = availableViewportSize();
    const maxX = Math.max(0, (output.offsetWidth * zoom.scale - available.width) / 2);
    const maxY = Math.max(0, (output.offsetHeight * zoom.scale - available.height) / 2);
    zoom.x = clamp(zoom.x, -maxX, maxX);
    zoom.y = clamp(zoom.y, -maxY, maxY);
  }

  function renderZoom(animate = false) {
    constrainPosition();
    viewport.classList.toggle('is-zoomed', zoom.scale > MIN_SCALE + 0.01);
    viewport.classList.toggle('is-zoom-animating', animate);
    output.style.setProperty('--cdg-scale', String(zoom.scale));
    output.style.setProperty('--cdg-x', `${zoom.x}px`);
    output.style.setProperty('--cdg-y', `${zoom.y}px`);
    zoomLevel.textContent = `${Math.round(zoom.scale * 100)}%`;
    if (animate) window.setTimeout(() => viewport.classList.remove('is-zoom-animating'), 180);
  }

  function resetZoom(animate = false) {
    zoom.scale = MIN_SCALE;
    zoom.x = 0;
    zoom.y = 0;
    pointers.clear();
    panStart = null;
    pinchStart = null;
    renderZoom(animate);
  }

  function setScale(nextScale, clientX, clientY, animate = false) {
    const previous = zoom.scale;
    const next = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    if (next === previous) return;
    if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
      const bounds = viewport.getBoundingClientRect();
      const focusX = clientX - bounds.left - bounds.width / 2;
      const focusY = clientY - bounds.top - bounds.height / 2;
      const ratio = next / previous;
      zoom.x = focusX - (focusX - zoom.x) * ratio;
      zoom.y = focusY - (focusY - zoom.y) * ratio;
    }
    zoom.scale = next;
    renderZoom(animate);
  }

  function refresh() {
    const selected = activeGalleryImage(activeRoot);
    if (!selected?.source) return false;
    resetZoom();
    output.alt = selected.alt;
    caption.textContent = selected.alt;
    if (output.src !== selected.source) output.src = selected.source;
    return true;
  }

  function open(root, trigger) {
    activeRoot = root;
    returnFocus = trigger || document.activeElement;
    if (!refresh()) return;
    document.body.classList.add('cdg-lightbox-open');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    requestAnimationFrame(() => resetZoom());
    dialog.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true });
  }

  function close() {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  function move(step) {
    if (!activeRoot) return;
    const arrow = activeRoot.querySelector(step < 0 ? '[data-gallery-prev]' : '[data-gallery-next]');
    if (!arrow) return;
    arrow.click();
    [80, 260, 480].forEach((delay) => window.setTimeout(refresh, delay));
  }

  function customPreviewAllowsOpen(root, stage, event) {
    if (!stage.classList.contains('is-custom-preview')) return true;
    const engine = root.querySelector('[data-craft-mask-engine]');
    const customizer = root.querySelector('craftdiy-customizer');
    const hitTest = typeof engine?.cpmHitTest === 'function'
      ? engine.cpmHitTest.bind(engine)
      : typeof customizer?.cpmHitTest === 'function'
        ? customizer.cpmHitTest.bind(customizer)
        : null;
    if (!hitTest) return true;
    try {
      return (hitTest(event.clientX, event.clientY) || 'preview') === 'preview';
    } catch (error) {
      return true;
    }
  }

  function isGallerySurface(target) {
    return target instanceof Element && Boolean(target.closest(
      'img,canvas,.cv9__swipe-slide,.ccr__swipe-slide,.cpm__stage,[data-custom-preview]',
    ));
  }

  function bind(root) {
    if (root.dataset.craftdiyGalleryZoomBound === 'true') return;
    root.dataset.craftdiyGalleryZoomBound = 'true';
    const stage = root.querySelector('[data-craftdiy-gallery-stage]');
    const trigger = root.querySelector('[data-gallery-zoom]');
    if (!stage || !trigger) return;

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      open(root, trigger);
    });

    let stagePointer = null;
    let blockClickUntil = 0;

    stage.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button,a,input,select,textarea,label,summary')) return;
      stagePointer = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: Date.now(),
        moved: false,
      };
    }, true);

    stage.addEventListener('pointermove', (event) => {
      if (!stagePointer || stagePointer.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - stagePointer.x, event.clientY - stagePointer.y) > 12) {
        stagePointer.moved = true;
      }
    }, true);

    stage.addEventListener('pointerup', (event) => {
      if (!stagePointer || stagePointer.id !== event.pointerId) return;
      const tap = !stagePointer.moved
        && Date.now() - stagePointer.time < 900
        && isGallerySurface(event.target)
        && customPreviewAllowsOpen(root, stage, event);
      stagePointer = null;
      if (!tap) {
        blockClickUntil = Date.now() + 450;
        return;
      }
      lastStageOpen = Date.now();
      blockClickUntil = lastStageOpen + 450;
      open(root, stage);
    }, true);

    stage.addEventListener('pointercancel', () => {
      stagePointer = null;
      blockClickUntil = Date.now() + 450;
    }, true);

    stage.addEventListener('click', (event) => {
      if (event.target.closest('button,a,input,select,textarea,label,summary')) return;
      if (Date.now() < blockClickUntil || Date.now() - lastStageOpen < 450) return;
      if (!isGallerySurface(event.target) || !customPreviewAllowsOpen(root, stage, event)) return;
      lastStageOpen = Date.now();
      open(root, stage);
    }, true);

    root.addEventListener('craftdiy:gallery-change', () => {
      if (dialog.open && activeRoot === root) window.setTimeout(refresh, 90);
    });
  }

  function pointDistance(first, second) {
    return Math.hypot(second.x - first.x, second.y - first.y);
  }

  function pointMidpoint(first, second) {
    return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
  }

  function beginPan(point) {
    panStart = { pointerX: point.x, pointerY: point.y, x: zoom.x, y: zoom.y };
    pinchStart = null;
  }

  function beginPinch() {
    const pair = [...pointers.values()].slice(0, 2);
    if (pair.length < 2) return;
    pinchStart = {
      distance: Math.max(1, pointDistance(pair[0], pair[1])),
      midpoint: pointMidpoint(pair[0], pair[1]),
      scale: zoom.scale,
      x: zoom.x,
      y: zoom.y,
    };
    panStart = null;
  }

  viewport.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    viewport.setPointerCapture?.(event.pointerId);
    gestureMoved = false;
    viewport.classList.add('is-interacting');
    if (pointers.size === 1) beginPan(pointers.values().next().value);
    if (pointers.size === 2) beginPinch();
    event.preventDefault();
  });

  viewport.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) return;
    const previous = pointers.get(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (Math.hypot(event.clientX - previous.x, event.clientY - previous.y) > 1) gestureMoved = true;

    if (pointers.size >= 2) {
      if (!pinchStart) beginPinch();
      const pair = [...pointers.values()].slice(0, 2);
      const midpoint = pointMidpoint(pair[0], pair[1]);
      zoom.scale = clamp(
        pinchStart.scale * pointDistance(pair[0], pair[1]) / pinchStart.distance,
        MIN_SCALE,
        MAX_SCALE,
      );
      zoom.x = pinchStart.x + midpoint.x - pinchStart.midpoint.x;
      zoom.y = pinchStart.y + midpoint.y - pinchStart.midpoint.y;
      renderZoom();
    } else if (pointers.size === 1 && panStart && zoom.scale > MIN_SCALE) {
      const point = pointers.values().next().value;
      zoom.x = panStart.x + point.x - panStart.pointerX;
      zoom.y = panStart.y + point.y - panStart.pointerY;
      renderZoom();
    }
    event.preventDefault();
  });

  function endPointer(event) {
    if (!pointers.has(event.pointerId)) return;
    pointers.delete(event.pointerId);
    viewport.releasePointerCapture?.(event.pointerId);
    if (pointers.size === 1) beginPan(pointers.values().next().value);
    if (!pointers.size) {
      panStart = null;
      pinchStart = null;
      viewport.classList.remove('is-interacting');
      renderZoom();
      if (!gestureMoved) close();
    }
  }

  viewport.addEventListener('pointerup', endPointer);
  viewport.addEventListener('pointercancel', endPointer);
  viewport.addEventListener('wheel', (event) => {
    event.preventDefault();
    setScale(zoom.scale + (event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP), event.clientX, event.clientY);
  }, { passive: false });

  output.addEventListener('load', () => resetZoom());
  output.addEventListener('dragstart', (event) => event.preventDefault());
  dialog.querySelector('[data-lightbox-close]').addEventListener('click', close);
  dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => move(-1));
  dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => move(1));
  dialog.querySelector('[data-lightbox-zoom-out]').addEventListener('click', () => setScale(zoom.scale - SCALE_STEP, undefined, undefined, true));
  dialog.querySelector('[data-lightbox-zoom-in]').addEventListener('click', () => setScale(zoom.scale + SCALE_STEP, undefined, undefined, true));
  dialog.querySelector('[data-lightbox-zoom-reset]').addEventListener('click', () => resetZoom(true));
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('close', () => {
    document.body.classList.remove('cdg-lightbox-open');
    output.removeAttribute('src');
    resetZoom();
    activeRoot = null;
    returnFocus?.focus?.({ preventScroll: true });
    returnFocus = null;
  });
  document.addEventListener('keydown', (event) => {
    if (!dialog.open) return;
    if (event.key === 'ArrowLeft') move(-1);
    if (event.key === 'ArrowRight') move(1);
    if (event.key === '+' || event.key === '=') setScale(zoom.scale + SCALE_STEP, undefined, undefined, true);
    if (event.key === '-') setScale(zoom.scale - SCALE_STEP, undefined, undefined, true);
    if (event.key === '0') resetZoom(true);
  });
  window.addEventListener('resize', () => { if (dialog.open) renderZoom(); }, { passive: true });

  document.querySelectorAll('[data-craftdiy-gallery-root]').forEach(bind);
})();
